import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AdminShell from "../../Components/admin/AdminShell";
import "../../Components/admin/adminCreateArticle.css";
import "./adminPosts.css";
import { fetchEventById, updateEvent } from "../../services/eventsApi";

export default function EditEvent() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [notFound, setNotFound] = useState(false);

  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("");
  const [tag, setTag] = useState("");
  const [date, setDate] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [authorRole, setAuthorRole] = useState("");
  const [featured, setFeatured] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setLoadError("");
      setNotFound(false);
      try {
        const ev = await fetchEventById(eventId);
        if (cancelled) return;
        if (!ev) {
          setNotFound(true);
          return;
        }
        setTitle(ev.title || "");
        setExcerpt(ev.excerpt || "");
        setContent(ev.content || ev.excerpt || "");
        setCategory(ev.category || "");
        setTag(ev.tag || "");
        setDate((ev.date || "").slice(0, 10));
        setImageUrl(ev.imageUrl || "");
        setAuthorName(ev.author?.name || "");
        setAuthorRole(ev.author?.role || "");
        setFeatured(!!ev.featured);
      } catch (e) {
        if (!cancelled) setLoadError("Failed to load event.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const save = async () => {
    setSaving(true);
    try {
      const result = await updateEvent(eventId, {
        title: title.trim(),
        excerpt: excerpt.trim(),
        content: content.trim(),
        category: category.trim(),
        tag: tag.trim(),
        date: date.trim() || "1970-01-01",
        imageUrl: imageUrl.trim(),
        author: { name: authorName.trim(), role: authorRole.trim() },
        featured,
      });
      if (result) navigate("/admin/posts");
    } catch (e) {
      setLoadError("Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  if (notFound) {
    return (
      <AdminShell title="Edit event" subtitle="Event not found">
        <div className="eventsError" style={{ maxWidth: 480 }}>
          <div className="eventsErrorTitle">Not found</div>
          <div className="eventsErrorMsg">This event does not exist or was removed.</div>
          <Link className="adminEventBtn" to="/admin/posts" style={{ marginTop: 12, display: "inline-block" }}>
            Back to posts
          </Link>
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell
      title="Edit event"
      subtitle="Update content shown on the Events page"
      actions={
        <div className="acaHeaderActions">
          <Link className="acaBtn acaBtnGhost" to="/admin/posts">
            Cancel
          </Link>
          <button type="button" className="acaBtn acaBtnPrimary" onClick={save} disabled={saving || loading}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      }
    >
      {loadError ? (
        <div className="eventsError" style={{ marginBottom: 16 }}>
          <div className="eventsErrorMsg">{loadError}</div>
        </div>
      ) : null}

      {loading ? (
        <div className="eventsFeatured eventsFeaturedLoading" aria-hidden="true">
          <div className="eventsSkelImg" style={{ maxWidth: 400 }} />
          <div className="eventsSkelRight">
            <div className="eventsSkelLine w40" />
            <div className="eventsSkelLine w90" />
            <div className="eventsSkelLine w85" />
          </div>
        </div>
      ) : (
        <div className="acaGrid">
          <div className="acaLeft">
            <div className="acaCard">
              <div className="acaCardHeader">Event content</div>
              <div className="acaCardBody">
                <label className="acaLabel" htmlFor="edTitle">
                  Title
                </label>
                <input id="edTitle" className="acaInput" value={title} onChange={(e) => setTitle(e.target.value)} />

                <label className="acaLabel" htmlFor="edExcerpt" style={{ marginTop: 14 }}>
                  Excerpt
                </label>
                <textarea
                  id="edExcerpt"
                  className="acaTextarea"
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  rows={3}
                />

                <label className="acaLabel" htmlFor="edContent" style={{ marginTop: 14 }}>
                  Full content
                </label>
                <textarea
                  id="edContent"
                  className="acaTextarea acaTextareaTall"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={8}
                />
              </div>
            </div>

            <div className="acaCard" style={{ marginTop: 16 }}>
              <div className="acaCardHeader">Image</div>
              <div className="acaCardBody">
                <label className="acaLabel" htmlFor="edImg">
                  Image URL
                </label>
                <input
                  id="edImg"
                  className="acaInput"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://…"
                />
              </div>
            </div>
          </div>

          <div className="acaRight">
            <div className="acaCard">
              <div className="acaCardHeader">Details</div>
              <div className="acaCardBody">
                <label className="acaLabel" htmlFor="edCat">
                  Category
                </label>
                <input id="edCat" className="acaInput" value={category} onChange={(e) => setCategory(e.target.value)} />

                <label className="acaLabel" htmlFor="edTag" style={{ marginTop: 12 }}>
                  Tag
                </label>
                <input id="edTag" className="acaInput" value={tag} onChange={(e) => setTag(e.target.value)} />

                <label className="acaLabel" htmlFor="edDate" style={{ marginTop: 12 }}>
                  Date
                </label>
                <input id="edDate" className="acaInput" type="date" value={date} onChange={(e) => setDate(e.target.value)} />

                <label className="acaLabel" htmlFor="edAuthName" style={{ marginTop: 12 }}>
                  Author name
                </label>
                <input id="edAuthName" className="acaInput" value={authorName} onChange={(e) => setAuthorName(e.target.value)} />

                <label className="acaLabel" htmlFor="edAuthRole" style={{ marginTop: 12 }}>
                  Author role
                </label>
                <input id="edAuthRole" className="acaInput" value={authorRole} onChange={(e) => setAuthorRole(e.target.value)} />

                <label className="acaLabel" style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                  <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} />
                  Featured (main spot on Events page)
                </label>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
